package com.writerslab.office

import androidx.compose.runtime.*
import kotlinx.coroutines.launch
import org.jetbrains.compose.web.attributes.*
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*

@Composable
fun CodesPage() {
    var codes by remember { mutableStateOf<List<OfficeCode>?>(null) }
    var error by remember { mutableStateOf<String?>(null) }
    var showGenForm by remember { mutableStateOf(false) }
    var genCount by remember { mutableStateOf("10") }
    var genCredits by remember { mutableStateOf("20") }
    var genNote by remember { mutableStateOf("") }
    var genResult by remember { mutableStateOf<List<String>?>(null) }
    var genError by remember { mutableStateOf<String?>(null) }
    var genLoading by remember { mutableStateOf(false) }
    val scope = rememberCoroutineScope()

    fun loadCodes() {
        scope.launch {
            error = null
            codes = null
            if (PREVIEW) { codes = MockApi.codes(); return@launch }
            try { codes = ApiClient.getCodes() }
            catch (e: Throwable) { error = e.message ?: "Ошибка загрузки" }
        }
    }

    LaunchedEffect(Unit) { loadCodes() }

    Div({ style { property("padding", "24px 0") } }) {
        Div({
            style {
                property("display", "flex")
                property("align-items", "center")
                property("gap", "12px")
                marginBottom(20.px)
            }
        }) {
            H2({ style { color(Color(C.OnSurface)); property("font-size", "18px"); margin(0.px) } }) {
                Text("Коды активации")
            }
            SmallButton("Сгенерировать") { showGenForm = !showGenForm; genResult = null; genError = null }
            SmallButton("Обновить") { loadCodes() }
        }

        if (showGenForm) {
            Div({
                style {
                    card()
                    property("max-width", "420px")
                    marginBottom(20.px)
                    property("display", "flex")
                    property("flex-direction", "column")
                    property("gap", "10px")
                }
            }) {
                Span({ style { label() } }) { Text("Генерация кодов") }
                FieldRow("Количество (1–500):") {
                    TextInput(genCount) { onInput { genCount = it.value }; style { fieldStyle() } }
                }
                FieldRow("Кредитов на код:") {
                    TextInput(genCredits) { onInput { genCredits = it.value }; style { fieldStyle() } }
                }
                FieldRow("Пометка (необязательно):") {
                    TextInput(genNote) { onInput { genNote = it.value }; style { fieldStyle() } }
                }
                Div({ style { property("display", "flex"); property("gap", "8px"); property("align-items", "center") } }) {
                    Button({
                        style { btn() }
                        if (genLoading) attr("disabled", "")
                        onClick {
                            scope.launch {
                                genLoading = true; genResult = null; genError = null
                                try {
                                    val resp = ApiClient.generateCodes(GenerateCodesRequest(
                                        count = genCount.toIntOrNull() ?: 0,
                                        credits = genCredits.toIntOrNull() ?: 0,
                                        batchNote = genNote.takeIf { it.isNotBlank() },
                                    ))
                                    genResult = resp.codes
                                    loadCodes()
                                } catch (e: Throwable) {
                                    genError = e.message ?: "Ошибка генерации"
                                } finally {
                                    genLoading = false
                                }
                            }
                        }
                    }) { Text(if (genLoading) "Генерация…" else "Создать") }
                    if (genError != null) Span({ style { color(Color(C.Error)); fontSize(13.px) } }) { Text(genError!!) }
                }
                if (genResult != null) {
                    Div({
                        style {
                            backgroundColor(Color(C.SurfaceHigh))
                            borderRadius(6.px)
                            padding(10.px)
                            property("max-height", "200px")
                            property("overflow-y", "auto")
                        }
                    }) {
                        Span({ style { label(); display(DisplayStyle.Block); marginBottom(6.px) } }) {
                            Text("${genResult!!.size} кодов создано:")
                        }
                        genResult!!.forEach { code ->
                            Div({ style { mono(); color(Color(C.Primary)); marginBottom(2.px) } }) { Text(code) }
                        }
                    }
                }
            }
        }

        if (error != null) {
            P({ style { color(Color(C.Error)) } }) { Text(error!!) }
            return@Div
        }
        if (codes == null) {
            P({ style { color(Color(C.Muted)) } }) { Text("Загрузка…") }
            return@Div
        }

        val list = codes!!
        Span({ style { color(Color(C.Muted)); fontSize(13.px); property("display", "block"); marginBottom(10.px) } }) {
            val avail = list.count { it.redeemedAt == null }
            Text("${list.size} кодов, ${avail} доступно")
        }

        Div({ style { property("overflow-x", "auto") } }) {
            Table({
                style { tableStyle() }
            }) {
                Thead {
                    Tr {
                        Th { Text("Код") }
                        Th { Text("Кредиты") }
                        Th { Text("Пометка") }
                        Th { Text("Создал") }
                        Th { Text("Создан") }
                        Th { Text("Статус") }
                        Th { Text("Использован кем") }
                    }
                }
                Tbody {
                    list.forEach { c ->
                        Tr {
                            Td { Span({ style { mono(); color(Color(C.Primary)) } }) { Text(c.code) } }
                            Td { Text(c.credits.toString()) }
                            Td { Text(c.batchNote ?: "—") }
                            Td {
                                Span({ style { mono(); fontSize(11.px); color(Color(C.Muted)) }
                                    if (c.createdBy != null) attr("title", c.createdBy)
                                }) { Text(c.createdBy?.take(12)?.let { if (c.createdBy.length > 12) "$it…" else it } ?: "—") }
                            }
                            Td { Text(c.createdAt.take(10)) }
                            Td {
                                val used = c.redeemedAt != null
                                Span({
                                    style {
                                        fontSize(12.px)
                                        property("font-weight", "600")
                                        color(Color(if (used) C.Muted else C.Success))
                                    }
                                }) { Text(if (used) "использован" else "доступен") }
                            }
                            Td {
                                Text(
                                    if (c.redeemedBy != null)
                                        c.redeemedBy.take(16) + (if (c.redeemedBy.length > 16) "…" else "")
                                    else "—"
                                )
                            }
                        }
                    }
                }
            }
        }
    }
}

@Composable
private fun FieldRow(labelText: String, content: @Composable () -> Unit) {
    Div({ style { property("display", "flex"); property("flex-direction", "column"); property("gap", "4px") } }) {
        Label { Span({ style { fontSize(12.px); color(Color(C.Muted)) } }) { Text(labelText) } }
        content()
    }
}

private fun StyleScope.fieldStyle() {
    width(100.percent)
    padding(7.px, 10.px)
    backgroundColor(Color(C.SurfaceHigh))
    color(Color(C.OnSurface))
    property("border", "1px solid ${C.Border}")
    borderRadius(6.px)
    property("font-size", "13px")
    property("outline", "none")
}

